import type { Edge, Node } from '../../../core/models/scene';
import { ORG_NODE_TYPES } from '../constants/orgNodeTypes';
import { resolveEffectiveShapeDimension } from '../../../core/shapes/effectiveDimensions';
import type { Point2D } from '../types/layout.types';
import type { LayoutStrategy } from './IOrgLayoutService';

/**
 * Connectors hidden by a view/lens are not part of the rendered graph and must
 * not affect hierarchy, spacing, or auto-layout change detection.
 */
export function isVisibleOrgLayoutConnector(edge: Edge): boolean {
  const attrs = edge.attrs as Record<string, unknown> | undefined;
  return attrs?.hidden !== true && attrs?.visible !== false;
}

export function isHierarchyLayoutEdge(edge: Edge): boolean {
  if (!isVisibleOrgLayoutConnector(edge)) return false;
  const edgeType = edge.type as string;
  if (edgeType === 'reports_to') return true;
  if (edgeType === 'reporting' || edgeType === 'team' || edgeType === 'occupies') return true;

  const relation = String((edge.attrs as Record<string, unknown> | undefined)?.relation ?? '');
  if (relation === 'reports_to') return true;
  return relation === 'reporting' || relation === 'team' || relation === 'occupies';
}

/**
 * Returns the effective layout type for org person nodes.
 * Uses actual node type so layout matches what is rendered.
 */
export function getEffectiveOrgPersonNodeType(node: Node | undefined): string {
  if (!node) return 'org_person_text';
  return node.type as string;
}

/**
 * Adjust height for org_person_photo nodes to account for top avatar overflow.
 * Exported for use in graphAnalysis (duplicated to avoid circular dependency).
 */
function adjustOrgPersonPhotoHeight(nodeType: string, height: number): number {
  const isCompact = height <= 100;
  const isFull = !isCompact;
  if (nodeType === 'org_person_photo') {
    const topInset = isCompact ? 65 : 36;
    return height + topInset;
  }
  if (nodeType === 'org_person_photo_left' || nodeType === 'org_person_photo_right') {
    if (isFull) return height + 36;
    return height;
  }
  return height;
}

/**
 * Calculate avatar overflow for left/right photo variants.
 * Returns padding needed on left and right sides to account for circular avatar extending beyond card bounds.
 * Exported for use in graphAnalysis (duplicated to avoid circular dependency).
 */
export function getAvatarOverflowPadding(nodeType: string, cardHeight: number): { left: number; right: number } {
  const isLeftVariant = nodeType === 'org_person_photo_left';
  const isRightVariant = nodeType === 'org_person_photo_right';
  
  if (!isLeftVariant && !isRightVariant) {
    return { left: 0, right: 0 };
  }
  
  // Avatar specs: avatarOuterSize = avatarSize + 10 = 72 + 10 = 82px (radius = 41px)
  // For compact side variants: avatar at x = -3 (left) or cardWidth + 3 (right)
  // Avatar extends: radius (41px) + offset (3px) = 44px beyond card edge
  const isCompact = cardHeight <= 100;
  const avatarSize = 72; // Same for compact and full
  const avatarOuterSize = avatarSize + 10; // 82px
  const avatarRadius = avatarOuterSize / 2; // 41px
  const avatarOffset = isCompact ? 3 : 0; // Compact variants have 3px offset
  const overflow = avatarRadius + avatarOffset; // 44px for compact, 41px for full
  const leftOverflow = isLeftVariant && isCompact ? overflow : 0;
  const rightOverflow = isRightVariant ? overflow : 0;

  return {
    left: leftOverflow,
    right: rightOverflow,
  };
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_NODE_WIDTH = 300;
const DEFAULT_NODE_HEIGHT = 180;
const DEFAULT_NODE_SPACING = 60;
export const DEFAULT_BRANCH_SPACING = 120;
const DEFAULT_VERTICAL_SPACING = 120;

// Vertical leaf stacking constants
export const LEAF_STACK_VERTICAL_GAP = 16;
export const LEAF_STACK_LEVEL_SPACING = 40;
export const ORG_DISPLAY_RULE_LABEL_CLEARANCE = 44;
/** Parent card bottom → shared horizontal bus line (CEO stem). */
export const ORG_BUS_STEM_GAP = 52;
/** Horizontal bus line → top of branch-row cards. */
export const ORG_BRANCH_ROW_BELOW_BUS_GAP = 68;
/** Total offset from visual parent bottom to branch-row card tops. */
export const ORG_BRANCH_ROW_OFFSET = ORG_BUS_STEM_GAP + ORG_BRANCH_ROW_BELOW_BUS_GAP;
export function leafStackIndent(parentWidth: number): number {
  return Math.round(parentWidth / 2) + 20;
}

/**
 * Floor for the vertical-spacing setting. Below this the reporting bus line and
 * its arrowhead cannot fit between a parent card and its children.
 */
export const MIN_ORG_LEVEL_GAP = 48;
/** Floor for the parent → first stacked-leaf gap. */
export const MIN_ORG_LEAF_LEVEL_GAP = 24;
/** Floor for the gap between two cards inside a vertical leaf stack. */
export const MIN_ORG_LEAF_STACK_GAP = 8;

export interface OrgLevelGaps {
  /** Parent visual bottom → branch-row child tops. */
  readonly branchLevelGap: number;
  /** Parent bottom → first stacked-leaf top. */
  readonly leafLevelGap: number;
  /** Gap between consecutive cards inside a vertical leaf stack. */
  readonly leafStackGap: number;
}

/**
 * Every vertical gap moves by the same number of pixels per step of the
 * vertical-spacing setting: branch rows below their parent, leaf stacks below
 * their parent, and cards within a leaf stack. Each keeps its own tighter or
 * wider default offset, so the chart's proportions survive while the setting
 * reads as one consistent control.
 */
export function resolveOrgLevelGaps(
  verticalSpacing: number,
  displayRuleLabelClearance = 0,
): OrgLevelGaps {
  const branchLevelGap = resolveOrgVerticalSpacing(verticalSpacing);
  const levelDelta = branchLevelGap - DEFAULT_VERTICAL_SPACING;
  const leafLevelGap = Math.max(
    MIN_ORG_LEAF_LEVEL_GAP,
    LEAF_STACK_LEVEL_SPACING + levelDelta,
  );
  const leafStackGap =
    Math.max(MIN_ORG_LEAF_STACK_GAP, LEAF_STACK_VERTICAL_GAP + levelDelta)
    + displayRuleLabelClearance;
  return { branchLevelGap, leafLevelGap, leafStackGap };
}

export function resolveOrgVerticalSpacing(verticalSpacing: number): number {
  if (!Number.isFinite(verticalSpacing)) return DEFAULT_VERTICAL_SPACING;
  return Math.max(MIN_ORG_LEVEL_GAP, Math.round(verticalSpacing));
}

export type TreeSpacingMode = 'legacy' | 'subtree-aware';

export interface TreeLayoutOptions {
  spacingMode?: TreeSpacingMode;
  nodeSpacing?: number;
  verticalSpacing?: number;
  branchSpacing?: number;
  minBranchGap?: number;
  maxBranchGap?: number;
  fanoutGapFactor?: number;
  sizeGapFactor?: number;
  rootGap?: number;
  displayRuleLabelClearance?: number;
}

export const DEFAULT_TREE_LAYOUT_OPTIONS: Required<TreeLayoutOptions> = {
  spacingMode: 'subtree-aware',
  nodeSpacing: DEFAULT_NODE_SPACING,
  verticalSpacing: DEFAULT_VERTICAL_SPACING,
  branchSpacing: DEFAULT_BRANCH_SPACING,
  minBranchGap: 80,
  maxBranchGap: 260,
  fanoutGapFactor: 12,
  sizeGapFactor: 0.18,
  rootGap: 80,
  displayRuleLabelClearance: 0,
};

function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

function resolveTreeLayoutOptions(options?: TreeLayoutOptions, nodeSpacing?: number): Required<TreeLayoutOptions> {
  const cleanOptions = options
    ? Object.fromEntries(Object.entries(options).filter(([_, v]) => v !== undefined && v !== null))
    : {};
  const merged = { ...DEFAULT_TREE_LAYOUT_OPTIONS, ...cleanOptions };
  if (typeof nodeSpacing === 'number' && Number.isFinite(nodeSpacing)) {
    merged.nodeSpacing = nodeSpacing;
  }
  merged.verticalSpacing = resolveOrgVerticalSpacing(merged.verticalSpacing);
  return merged;
}

function computeBranchGap(
  parentId: string,
  childIds: string[],
  nodeById: Map<string, Node>,
  options: Required<TreeLayoutOptions>,
): number {
  if (options.spacingMode === 'legacy') {
    return options.branchSpacing;
  }

  if (childIds.length <= 1) {
    return options.minBranchGap;
  }

  const parentWidth = getNodeWidth(nodeById.get(parentId));
  let totalChildWidth = 0;
  let maxChildWidth = 0;
  childIds.forEach((childId) => {
    const width = getNodeWidth(nodeById.get(childId));
    totalChildWidth += width;
    maxChildWidth = Math.max(maxChildWidth, width);
  });

  const avgChildWidth = totalChildWidth / childIds.length;
  const fanoutTerm = Math.sqrt(childIds.length - 1) * options.fanoutGapFactor;
  const sizeAnchor = Math.max(parentWidth, avgChildWidth, maxChildWidth * 0.8);
  const sizeTerm = sizeAnchor * options.sizeGapFactor;
  const baseGap = options.nodeSpacing + fanoutTerm + sizeTerm;

  return clamp(Math.round(baseGap), options.minBranchGap, options.maxBranchGap);
}

function getSubtreeHorizontalBounds(
  rootId: string,
  hierarchy: HierarchyMaps,
  nodeById: Map<string, Node>,
  currentPositions: Map<string, Point2D>,
  newPositions: Map<string, Point2D>,
): { left: number; right: number } {
  let left = Infinity;
  let right = -Infinity;
  const stack = [rootId];
  const visited = new Set<string>();

  while (stack.length > 0) {
    const nodeId = stack.pop()!;
    if (visited.has(nodeId)) continue;
    visited.add(nodeId);
    const pos = newPositions.get(nodeId) ?? currentPositions.get(nodeId);
    if (pos) {
      const width = getNodeWidth(nodeById.get(nodeId));
      left = Math.min(left, pos.x);
      right = Math.max(right, pos.x + width);
    }

    const children = hierarchy.childrenByParent.get(nodeId) ?? [];
    for (let i = 0; i < children.length; i += 1) {
      stack.push(children[i]);
    }
  }

  if (!Number.isFinite(left) || !Number.isFinite(right)) {
    return { left: 0, right: 0 };
  }
  return { left, right };
}

/**
 * Analysis result for an org chart graph shape
 */
export interface GraphShapeAnalysis {
  /** Maximum number of nodes at any single level (horizontal breadth) */
  maxBreadth: number;
  /** Maximum depth of the hierarchy (number of levels) */
  maxDepth: number;
  /** Whether there's a breadth hotspot (one level significantly wider than others) */
  hasBreadthHotspot: boolean;
  /** The level index with the most nodes */
  widestLevel: number;
  /** Number of root nodes (nodes with no parent) */
  rootCount: number;
  /** Total number of nodes */
  totalNodes: number;
  /** Recommended layout strategy based on shape */
  recommendedStrategy: LayoutStrategy;
}

/**
 * Analyzes the shape of an org chart graph to determine optimal layout strategy.
 *
 * @param nodes - All org chart nodes
 * @param edges - All edges (will filter to org edges)
 * @returns Analysis of the graph shape
 */
export function analyzeGraphShape(nodes: Node[], edges: Edge[]): GraphShapeAnalysis {
  const orgNodes = nodes.filter(node => ORG_NODE_TYPES.has(node.type as string));

  if (orgNodes.length === 0) {
    return {
      maxBreadth: 0,
      maxDepth: 0,
      hasBreadthHotspot: false,
      widestLevel: 0,
      rootCount: 0,
      totalNodes: 0,
      recommendedStrategy: 'auto',
    };
  }

  const orgEdges = edges.filter(isHierarchyLayoutEdge);

  // Build parent-child relationships (for all org edge types)
  const childrenByParent = new Map<string, string[]>();
  const nodeHasParent = new Set<string>();

  orgEdges.forEach(edge => {
    if (isHierarchyLayoutEdge(edge)) {
      const children = childrenByParent.get(edge.to) ?? [];
      children.push(edge.from);
      childrenByParent.set(edge.to, children);
      nodeHasParent.add(edge.from);
    }
  });

  // Find root nodes (no parent)
  const rootNodes = orgNodes.filter(node => !nodeHasParent.has(node.id));
  const rootCount = rootNodes.length;

  // Calculate depth and breadth by level using BFS
  const levelByNode = new Map<string, number>();
  const nodesByLevel = new Map<number, string[]>();

  // Initialize with root nodes at level 0
  rootNodes.forEach(node => {
    levelByNode.set(node.id, 0);
    const level0Nodes = nodesByLevel.get(0) ?? [];
    level0Nodes.push(node.id);
    nodesByLevel.set(0, level0Nodes);
  });

  // BFS to assign levels
  const queue = [...rootNodes.map(n => n.id)];
  while (queue.length > 0) {
    const nodeId = queue.shift()!;
    const currentLevel = levelByNode.get(nodeId) ?? 0;
    const children = childrenByParent.get(nodeId) ?? [];

    children.forEach(childId => {
      if (!levelByNode.has(childId)) {
        const childLevel = currentLevel + 1;
        levelByNode.set(childId, childLevel);

        const levelNodes = nodesByLevel.get(childLevel) ?? [];
        levelNodes.push(childId);
        nodesByLevel.set(childLevel, levelNodes);

        queue.push(childId);
      }
    });
  }

  // Calculate metrics
  const levels = Array.from(nodesByLevel.entries()).sort((a, b) => a[0] - b[0]);
  const maxDepth = levels.length;

  let maxBreadth = 0;
  let widestLevel = 0;
  const breadthByLevel: number[] = [];

  levels.forEach(([level, nodeIds]) => {
    const breadth = nodeIds.length;
    breadthByLevel.push(breadth);
    if (breadth > maxBreadth) {
      maxBreadth = breadth;
      widestLevel = level;
    }
  });

  // Detect breadth hotspot (one level is 2x+ wider than average of others)
  let hasBreadthHotspot = false;
  if (breadthByLevel.length > 1) {
    const otherLevels = breadthByLevel.filter((_, i) => i !== widestLevel);
    const avgOtherBreadth = otherLevels.reduce((a, b) => a + b, 0) / otherLevels.length;
    hasBreadthHotspot = maxBreadth >= avgOtherBreadth * 2;
  }

  // Determine recommended strategy
  let recommendedStrategy: LayoutStrategy = 'top-down';

  if (maxBreadth > 6) {
    // Wide graph - recommend wrapping
    recommendedStrategy = 'wrapped';
  } else if (maxDepth > maxBreadth * 2) {
    // Deep and narrow - consider left-right
    recommendedStrategy = 'left-right';
  }

  return {
    maxBreadth,
    maxDepth,
    hasBreadthHotspot,
    widestLevel,
    rootCount,
    totalNodes: orgNodes.length,
    recommendedStrategy,
  };
}

/**
 * Groups siblings into rows for wrapped layout.
 *
 * @param siblings - Array of sibling node IDs
 * @param maxPerRow - Maximum siblings per row
 * @returns Array of rows, each containing node IDs
 */
export function groupSiblingsIntoRows(siblings: string[], maxPerRow: number = 6): string[][] {
  const rows: string[][] = [];
  for (let i = 0; i < siblings.length; i += maxPerRow) {
    rows.push(siblings.slice(i, i + maxPerRow));
  }
  return rows;
}

// ============================================================================
// Incremental Layout Utilities
// ============================================================================

/**
 * Type guard to check if a node is an org chart node
 */
export function isOrgNode(node: Node): boolean {
  return ORG_NODE_TYPES.has(node.type as string);
}

/**
 * Gets the width of a node, handling both 'width' and legacy 'w' properties.
 * Returns DEFAULT_NODE_WIDTH (270) if node is undefined or has no width.
 */
export function getNodeWidth(node: Node | undefined): number {
  if (!node) return DEFAULT_NODE_WIDTH;
  const rawWidth = resolveEffectiveShapeDimension(node, 'width') ?? DEFAULT_NODE_WIDTH;
  const effectiveType = getEffectiveOrgPersonNodeType(node);
  const height = resolveEffectiveShapeDimension(node, 'height') ?? DEFAULT_NODE_HEIGHT;
  const overflowPadding = getAvatarOverflowPadding(effectiveType, height);
  return rawWidth + overflowPadding.left + overflowPadding.right;
}

/**
 * Gets the height of a node, handling both 'height' and legacy 'h' properties.
 * Returns DEFAULT_NODE_HEIGHT (80) if node is undefined or has no height.
 * Accounts for avatar overflow (top padding for center-aligned variants).
 */
export function getNodeVisualHeight(node: Node | undefined): number {
  if (!node) return DEFAULT_NODE_HEIGHT;
  const rawHeight = resolveEffectiveShapeDimension(node, 'height') ?? DEFAULT_NODE_HEIGHT;
  const effectiveType = getEffectiveOrgPersonNodeType(node);
  return adjustOrgPersonPhotoHeight(effectiveType, rawHeight);
}

export function getNodeHeight(
  node: Node | undefined,
  displayRuleLabelClearance = 0,
): number {
  return getNodeVisualHeight(node) + displayRuleLabelClearance;
}

function getLeafStackAdvanceHeight(node: Node | undefined): number {
  if (!node) return DEFAULT_NODE_HEIGHT;
  const rawHeight = resolveEffectiveShapeDimension(node, 'height') ?? DEFAULT_NODE_HEIGHT;
  const effectiveType = getEffectiveOrgPersonNodeType(node);
  if (effectiveType === 'org_person_photo' && rawHeight <= 100) {
    return rawHeight;
  }
  return getNodeVisualHeight(node);
}

// ============================================================================
// Leaf Detection Utilities (for vertical leaf stacking)
// ============================================================================

/**
 * Returns true if the given node is a leaf (has no children).
 * A node is considered a leaf if it has no outgoing edges from its bottom.
 * Collapsed nodes are never considered leaves even if their children are hidden.
 */
export function isLeafNode(nodeId: string, hierarchy: HierarchyMaps): boolean {
  // If node has ANY outgoing edges from its bottom, it's not a leaf
  if (hierarchy.nodesWithOutgoingEdges.has(nodeId)) {
    return false;
  }
  // Fallback: check org edge children (for backward compatibility)
  const children = hierarchy.childrenByParent.get(nodeId) ?? [];
  return children.length === 0;
}

/**
 * Returns children of nodeId that are leaves (have no children of their own).
 */
export function getLeafChildren(nodeId: string, hierarchy: HierarchyMaps): string[] {
  const children = hierarchy.childrenByParent.get(nodeId) ?? [];
  return children.filter(childId => isLeafNode(childId, hierarchy));
}

/**
 * Returns children of nodeId that are branches (have their own children).
 */
export function getBranchChildren(nodeId: string, hierarchy: HierarchyMaps): string[] {
  const children = hierarchy.childrenByParent.get(nodeId) ?? [];
  return children.filter(childId => !isLeafNode(childId, hierarchy));
}

/**
 * Returns true if nodeId has at least one leaf child.
 */
export function hasLeafChildren(nodeId: string, hierarchy: HierarchyMaps): boolean {
  const children = hierarchy.childrenByParent.get(nodeId) ?? [];
  return children.some(childId => isLeafNode(childId, hierarchy));
}

/**
 * Returns true if all children of nodeId are leaves (no grandchildren).
 */
export function isLeafParent(nodeId: string, hierarchy: HierarchyMaps): boolean {
  const children = hierarchy.childrenByParent.get(nodeId) ?? [];
  if (children.length === 0) return false;
  return children.every(childId => isLeafNode(childId, hierarchy));
}

/**
 * Computes the subtree width for a single node, accounting for vertical leaf stacking.
 * Used by both calculateSubtreeWidths and findHighestAffectedAncestor for consistency.
 */
export function computeNodeSubtreeWidth(
  nodeId: string,
  hierarchy: HierarchyMaps,
  widths: Map<string, number>,
  nodeById: Map<string, Node>,
  _nodeSpacing: number = DEFAULT_NODE_SPACING,
  subtreeOffsets?: Map<string, number>,
  options?: TreeLayoutOptions,
): number {
  const layoutOptions = resolveTreeLayoutOptions(options, _nodeSpacing);
  const children = hierarchy.childrenByParent.get(nodeId) ?? [];
  const node = nodeById.get(nodeId);
  const nodeWidth = getNodeWidth(node);

  // CASE 1: No children → just node width
  if (children.length === 0) {
    subtreeOffsets?.set(nodeId, 0);
    return nodeWidth;
  }

  const stackedChildren = getLeafChildren(nodeId, hierarchy);
  const expandedChildren = getBranchChildren(nodeId, hierarchy);

  // Max width among stacked children
  let maxStackedChildWidth = 0;
  for (const childId of stackedChildren) {
    maxStackedChildWidth = Math.max(maxStackedChildWidth, widths.get(childId) ?? DEFAULT_NODE_WIDTH);
  }

  // CASE 2: All stacked (no expanded children)
  // Node at left, stack extends rightward from parent midpoint + 20
  if (expandedChildren.length === 0) {
    const indent = leafStackIndent(nodeWidth);
    const w = Math.max(nodeWidth, indent + maxStackedChildWidth);
    subtreeOffsets?.set(nodeId, 0);
    return w;
  }

  // CASE 3: Has expanded children
  if (stackedChildren.length > 0) {
    // MIXED: center stack at parent midpoint + 20, expanded balanced around it
    // 1st expanded → left, 2nd → right, etc.
    const indent = leafStackIndent(nodeWidth);
    const branchGap = computeBranchGap(nodeId, expandedChildren, nodeById, layoutOptions);
    const leftCount = Math.ceil(expandedChildren.length / 2);
    const leftHalf = expandedChildren.slice(0, leftCount);
    const rightHalf = expandedChildren.slice(leftCount);

    let leftWidth = 0;
    leftHalf.forEach((childId, idx) => {
      leftWidth += widths.get(childId) ?? DEFAULT_NODE_WIDTH;
      if (idx < leftHalf.length - 1) leftWidth += branchGap;
    });

    let rightWidth = 0;
    rightHalf.forEach((childId, idx) => {
      rightWidth += widths.get(childId) ?? DEFAULT_NODE_WIDTH;
      if (idx < rightHalf.length - 1) rightWidth += branchGap;
    });

    // Edges relative to parent's left edge (pos.x = 0)
    const leftEdge = leftWidth > 0
      ? indent - branchGap - leftWidth
      : 0;
    const stackRightEdge = indent + maxStackedChildWidth;
    const rightEdge = rightWidth > 0
      ? stackRightEdge + branchGap + rightWidth
      : Math.max(nodeWidth, stackRightEdge);

    // Actual width and offset (no symmetric inflation)
    const actualLeftEdge = Math.min(0, leftEdge);
    const w = rightEdge - actualLeftEdge;
    subtreeOffsets?.set(nodeId, -actualLeftEdge);
    return Math.max(nodeWidth, w);
  }

  // CASE 4: ALL EXPANDED (no stacked) — balanced left/right centered under parent
  const branchGap = computeBranchGap(nodeId, expandedChildren, nodeById, layoutOptions);
  const leftCount = Math.floor(expandedChildren.length / 2);
  const leftHalf = expandedChildren.slice(0, leftCount);
  const rightHalf = expandedChildren.slice(leftCount);

  let leftWidth = 0;
  leftHalf.forEach((childId, idx) => {
    leftWidth += widths.get(childId) ?? DEFAULT_NODE_WIDTH;
    if (idx < leftHalf.length - 1) leftWidth += branchGap;
  });

  let rightWidth = 0;
  rightHalf.forEach((childId, idx) => {
    rightWidth += widths.get(childId) ?? DEFAULT_NODE_WIDTH;
    if (idx < rightHalf.length - 1) rightWidth += branchGap;
  });

  const gap = leftWidth > 0 && rightWidth > 0 ? branchGap : 0;
  const totalWidth = leftWidth + gap + rightWidth;
  const w = Math.max(nodeWidth, totalWidth);
  subtreeOffsets?.set(nodeId, (w - nodeWidth) / 2);
  return w;
}

/**
 * Parent-child relationship maps for traversing org chart hierarchy
 */
export interface HierarchyMaps {
  /** Maps parent ID to array of child IDs (org edges only, for hierarchy) */
  childrenByParent: Map<string, string[]>;
  /** Maps child ID to parent ID */
  parentByChild: Map<string, string>;
  /** Set of root node IDs (nodes with no parent) */
  rootIds: Set<string>;
  /** Set of node IDs that have ANY outgoing edges from bottom (for leaf detection) */
  nodesWithOutgoingEdges: Set<string>;
}

/**
 * Builds parent-child relationship maps from edges.
 * Considers hierarchy-defining org edge types (reporting, team, occupies) for hierarchy.
 * Dotted and matrix edges are excluded from hierarchy so secondary relationships don't
 * change primary tree structure during layout.
 * Children are sorted by their current X position to preserve visual order.
 * Falls back to edge ID for new nodes without positions.
 *
 * @param orgNodeIds - Set of org node IDs
 * @param edges - All edges
 * @param currentPositions - Optional map of node positions (for preserving visual order)
 */
export function buildHierarchyMaps(
  orgNodeIds: Set<string>,
  edges: Edge[],
  currentPositions?: Map<string, Point2D>
): HierarchyMaps {
  const childrenByParent = new Map<string, string[]>();
  const parentByChild = new Map<string, string>();
  // Track edge ID for each child to enable stable sorting (fallback)
  const edgeIdByChild = new Map<string, string>();
  // Track nodes that have ANY outgoing edges from bottom (for leaf detection)
  const nodesWithOutgoingEdges = new Set<string>();

  edges.forEach(edge => {
    const hierarchyEdge = isHierarchyLayoutEdge(edge);
    // Only hierarchy-defining relationships affect leaf stacking. Overlay and
    // planning relationships such as successor_for must remain layout-neutral.
    if (hierarchyEdge && orgNodeIds.has(edge.to)) {
      nodesWithOutgoingEdges.add(edge.to);
    }

    if (hierarchyEdge) {
      const childId = edge.from;
      const parentId = edge.to;

      if (!orgNodeIds.has(childId) || !orgNodeIds.has(parentId)) return;

      parentByChild.set(childId, parentId);
      edgeIdByChild.set(childId, edge.id);

      const children = childrenByParent.get(parentId) ?? [];
      children.push(childId);
      childrenByParent.set(parentId, children);
    }
  });

  // Sort children by current X position (preserves visual order from ELK or manual layout)
  // Falls back to edge ID for new nodes without positions
  childrenByParent.forEach((children) => {
    children.sort((a, b) => {
      const posA = currentPositions?.get(a);
      const posB = currentPositions?.get(b);

      // Both have positions: sort by X
      if (posA && posB) {
        return posA.x - posB.x;
      }

      // Only one has position: positioned node comes first
      if (posA && !posB) return -1;
      if (!posA && posB) return 1;

      // Neither has position: fall back to edge ID (creation order)
      const edgeA = edgeIdByChild.get(a) ?? '';
      const edgeB = edgeIdByChild.get(b) ?? '';
      return edgeA.localeCompare(edgeB);
    });

  });

  // Initialize empty children arrays for leaf nodes
  orgNodeIds.forEach(nodeId => {
    if (!childrenByParent.has(nodeId)) {
      childrenByParent.set(nodeId, []);
    }
  });

  // Detect and break cycles: walk ancestor chain for each node; if we revisit
  // a node we have a cycle — sever the back-edge to keep the tree acyclic.
  const breakCycles = () => {
    for (const nodeId of orgNodeIds) {
      const visited = new Set<string>();
      let cur = nodeId;
      while (cur) {
        if (visited.has(cur)) {
          const parentId = parentByChild.get(cur);
          if (parentId !== undefined) {
            parentByChild.delete(cur);
            const siblings = childrenByParent.get(parentId);
            if (siblings) {
              const idx = siblings.indexOf(cur);
              if (idx !== -1) siblings.splice(idx, 1);
            }
          }
          break;
        }
        visited.add(cur);
        const parent = parentByChild.get(cur);
        if (!parent) break;
        cur = parent;
      }
    }
  };
  breakCycles();

  // Find roots (nodes without parents)
  const rootIds = new Set<string>();
  orgNodeIds.forEach(nodeId => {
    if (!parentByChild.has(nodeId)) {
      rootIds.add(nodeId);
    }
  });

  return { childrenByParent, parentByChild, rootIds, nodesWithOutgoingEdges };
}

/**
 * Calculates subtree widths for all nodes using bottom-up (post-order) traversal.
 * Uses iterative approach to avoid stack overflow on deep trees.
 *
 * @param hierarchy - Parent-child relationship maps
 * @param nodeById - Map of node ID to Node object
 * @param nodeSpacing - Horizontal spacing between sibling nodes (default: 40)
 * @returns Map of node ID to subtree width
 */
export function calculateSubtreeWidths(
  hierarchy: HierarchyMaps,
  nodeById: Map<string, Node>,
  nodeSpacing: number = DEFAULT_NODE_SPACING,
  subtreeOffsets?: Map<string, number>,
  options?: TreeLayoutOptions,
): Map<string, number> {
  const widths = new Map<string, number>();

  // Iterative post-order traversal using explicit stack
  // Phase: 'descend' = first visit, 'process' = all children done
  const stack: Array<{ nodeId: string; phase: 'descend' | 'process' }> = [];
  const descended = new Set<string>();

  // Start from all roots
  hierarchy.rootIds.forEach(rootId => {
    stack.push({ nodeId: rootId, phase: 'descend' });
  });

  while (stack.length > 0) {
    const { nodeId, phase } = stack.pop()!;
    const children = hierarchy.childrenByParent.get(nodeId) ?? [];

    if (phase === 'descend') {
      if (descended.has(nodeId)) continue;
      descended.add(nodeId);
      // First visit: push self for processing, then push children
      stack.push({ nodeId, phase: 'process' });

      // Push children in reverse order so they're processed left-to-right
      for (let i = children.length - 1; i >= 0; i--) {
        stack.push({ nodeId: children[i], phase: 'descend' });
      }
    } else {
      // Second visit: all children processed, calculate this node's width
      if (children.length === 0) {
        const node = nodeById.get(nodeId);
        widths.set(nodeId, getNodeWidth(node));
        subtreeOffsets?.set(nodeId, 0);
      } else {
        widths.set(nodeId, computeNodeSubtreeWidth(nodeId, hierarchy, widths, nodeById, nodeSpacing, subtreeOffsets, options));
      }
    }
  }

  return widths;
}

/**
 * Calculates new positions for children of a parent node, centering them under the parent.
 * Also recursively positions grandchildren.
 *
 * @param parentId - ID of the parent node
 * @param parentPos - Current position of the parent node
 * @param hierarchy - Parent-child relationship maps
 * @param nodeById - Map of node ID to Node object
 * @param widths - Subtree widths from calculateSubtreeWidths
 * @param currentPositions - Current positions of all nodes
 * @param nodeSpacing - Horizontal spacing between siblings
 * @returns Map of node ID to new position (only includes nodes that need to move)
 */
export function recenterChildren(
  parentId: string,
  parentPos: Point2D,
  hierarchy: HierarchyMaps,
  nodeById: Map<string, Node>,
  widths: Map<string, number>,
  currentPositions: Map<string, Point2D>,
  _nodeSpacing: number = DEFAULT_NODE_SPACING,
  subtreeOffsets?: Map<string, number>,
  options?: TreeLayoutOptions,
): Map<string, Point2D> {
  const layoutOptions = resolveTreeLayoutOptions(options, _nodeSpacing);
  const levelGaps = resolveOrgLevelGaps(
    layoutOptions.verticalSpacing,
    layoutOptions.displayRuleLabelClearance,
  );
  const currentLeafVerticalGap = levelGaps.leafStackGap;
  const currentLeafLevelSpacing = levelGaps.leafLevelGap;

  const newPositions = new Map<string, Point2D>();
  const POSITION_EPSILON = 1; // Ignore sub-pixel differences

  const placeChild = (childId: string, targetX: number, targetY: number) => {
    const childCurrentPos = newPositions.get(childId) ?? currentPositions.get(childId);
    if (childCurrentPos) {
      const deltaX = targetX - childCurrentPos.x;
      const deltaY = targetY - childCurrentPos.y;
      if (Math.abs(deltaX) > POSITION_EPSILON || Math.abs(deltaY) > POSITION_EPSILON) {
        moveSubtreeXY(childId, deltaX, deltaY, hierarchy, currentPositions, newPositions);
      }
    } else {
      newPositions.set(childId, { x: targetX, y: targetY });
    }
  };

  const positionLeafStack = (leafIds: string[], stackX: number, startY: number, alignWithBranchRow = false) => {
    let currentY = startY;
    const centerCompactOffset = 41 + 20;
    const fullLeftRightTopOffset = 36;
    for (const leafId of leafIds) {
      const leafNode = nodeById.get(leafId);
      const rawH = leafNode
        ? (resolveEffectiveShapeDimension(leafNode, 'height') ?? DEFAULT_NODE_HEIGHT)
        : DEFAULT_NODE_HEIGHT;
      const layoutType = leafNode ? getEffectiveOrgPersonNodeType(leafNode) : 'org_person_text';
      const isCenterCompact = leafNode && layoutType === 'org_person_photo' && rawH <= 100;
      const isFullLeftOrRight = leafNode && (layoutType === 'org_person_photo_left' || layoutType === 'org_person_photo_right') && rawH > 100;
      const advanceHeight = getLeafStackAdvanceHeight(leafNode);
      const overflowPadding = leafNode ? getAvatarOverflowPadding(layoutType, rawH) : { left: 0, right: 0 };
      const adjustedX = stackX + overflowPadding.left;
      const photoTopOffset = alignWithBranchRow
        ? 0
        : (isCenterCompact ? centerCompactOffset : isFullLeftOrRight ? fullLeftRightTopOffset : 0);
      const placeY = currentY + photoTopOffset;
      placeChild(leafId, adjustedX, placeY);
      if (!alignWithBranchRow && isCenterCompact) currentY += centerCompactOffset;
      if (!alignWithBranchRow && isFullLeftOrRight) currentY += fullLeftRightTopOffset;
      currentY += advanceHeight + currentLeafVerticalGap;
    }
  };

  const visitedNodes = new Set<string>();
  const processNode = (nodeId: string, pos: Point2D) => {
    if (visitedNodes.has(nodeId)) return;
    visitedNodes.add(nodeId);
    const children = hierarchy.childrenByParent.get(nodeId) ?? [];
    if (children.length === 0) return;

    const node = nodeById.get(nodeId);
    const nodeWidth = getNodeWidth(node);
    const nodeHeight = getNodeHeight(node, layoutOptions.displayRuleLabelClearance);
    const branchRowBaseY = pos.y + getNodeVisualHeight(node);
    const parentCenterX = pos.x + nodeWidth / 2;
    const childrenY = branchRowBaseY + levelGaps.branchLevelGap;
    const leafChildrenY = pos.y + nodeHeight + currentLeafLevelSpacing;

    const stacked = getLeafChildren(nodeId, hierarchy);
    const expanded = getBranchChildren(nodeId, hierarchy);

    // CASE: All stacked (no expanded children)
    if (expanded.length === 0) {
      const stackX = pos.x + leafStackIndent(nodeWidth);
      positionLeafStack(stacked, stackX, leafChildrenY);
      return;
    }

    // CASE: Has expanded children
    let maxStackedChildWidth = 0;
    for (const childId of stacked) {
      maxStackedChildWidth = Math.max(maxStackedChildWidth, getNodeWidth(nodeById.get(childId)));
    }

    if (stacked.length > 0) {
      // MIXED: center stack at parent midpoint + 20, expanded balanced around it
      // 1st expanded → left, 2nd → right, etc.
      const stackX = pos.x + leafStackIndent(nodeWidth);
      positionLeafStack(stacked, stackX, leafChildrenY);
      const branchGap = computeBranchGap(nodeId, expanded, nodeById, layoutOptions);

      const leftCount = Math.ceil(expanded.length / 2);
      const leftHalf = expanded.slice(0, leftCount);
      const rightHalf = expanded.slice(leftCount);

      // Position RIGHT expanded (grow rightward from stack right edge)
      let rightX = stackX + maxStackedChildWidth + branchGap;
      for (const childId of rightHalf) {
        const subtreeWidth = widths.get(childId) ?? DEFAULT_NODE_WIDTH;
        const offset = subtreeOffsets?.get(childId) ?? (subtreeWidth - getNodeWidth(nodeById.get(childId))) / 2;
        const targetX = rightX + offset;
        placeChild(childId, targetX, childrenY);
        rightX += subtreeWidth + branchGap;

        const childPos = newPositions.get(childId) ?? currentPositions.get(childId);
        if (childPos) processNode(childId, childPos);
      }

      // Position LEFT expanded (grow leftward from stack left edge)
      if (leftHalf.length > 0) {
        let totalLeftWidth = 0;
        leftHalf.forEach((childId, idx) => {
          totalLeftWidth += widths.get(childId) ?? DEFAULT_NODE_WIDTH;
          if (idx < leftHalf.length - 1) totalLeftWidth += branchGap;
        });

        let leftX = stackX - branchGap - totalLeftWidth;
        for (const childId of leftHalf) {
          const subtreeWidth = widths.get(childId) ?? DEFAULT_NODE_WIDTH;
          const offset = subtreeOffsets?.get(childId) ?? (subtreeWidth - getNodeWidth(nodeById.get(childId))) / 2;
          const targetX = leftX + offset;
          placeChild(childId, targetX, childrenY);
          leftX += subtreeWidth + branchGap;

          const childPos = newPositions.get(childId) ?? currentPositions.get(childId);
          if (childPos) processNode(childId, childPos);
        }
      }
    } else {
      // ALL EXPANDED: balanced left/right centered under parent
      const branchGap = computeBranchGap(nodeId, expanded, nodeById, layoutOptions);
      const leftCount = Math.floor(expanded.length / 2);
      const leftHalf = expanded.slice(0, leftCount);
      const rightHalf = expanded.slice(leftCount);

      let leftWidth = 0;
      leftHalf.forEach((childId, idx) => {
        leftWidth += widths.get(childId) ?? DEFAULT_NODE_WIDTH;
        if (idx < leftHalf.length - 1) leftWidth += branchGap;
      });

      let rightWidth = 0;
      rightHalf.forEach((childId, idx) => {
        rightWidth += widths.get(childId) ?? DEFAULT_NODE_WIDTH;
        if (idx < rightHalf.length - 1) rightWidth += branchGap;
      });

      const gap = leftWidth > 0 && rightWidth > 0 ? branchGap : 0;
      const totalWidth = leftWidth + gap + rightWidth;
      let currentX = parentCenterX - totalWidth / 2;

      // Position LEFT expanded
      for (const childId of leftHalf) {
        const subtreeWidth = widths.get(childId) ?? DEFAULT_NODE_WIDTH;
        const offset = subtreeOffsets?.get(childId) ?? (subtreeWidth - getNodeWidth(nodeById.get(childId))) / 2;
        const targetX = currentX + offset;
        placeChild(childId, targetX, childrenY);
        currentX += subtreeWidth + branchGap;

        const childPos = newPositions.get(childId) ?? currentPositions.get(childId);
        if (childPos) processNode(childId, childPos);
      }

      // Position RIGHT expanded
      for (const childId of rightHalf) {
        const subtreeWidth = widths.get(childId) ?? DEFAULT_NODE_WIDTH;
        const offset = subtreeOffsets?.get(childId) ?? (subtreeWidth - getNodeWidth(nodeById.get(childId))) / 2;
        const targetX = currentX + offset;
        placeChild(childId, targetX, childrenY);
        currentX += subtreeWidth + branchGap;

        const childPos = newPositions.get(childId) ?? currentPositions.get(childId);
        if (childPos) processNode(childId, childPos);
      }
    }

    // After local placement, enforce non-overlap between adjacent sibling subtrees.
    // This prevents deep descendants from bleeding into neighboring branches.
    if (expanded.length > 1) {
      const sortedChildren = [...expanded].sort((a, b) => {
        const posA = newPositions.get(a) ?? currentPositions.get(a);
        const posB = newPositions.get(b) ?? currentPositions.get(b);
        return (posA?.x ?? 0) - (posB?.x ?? 0);
      });
      const requiredGap = layoutOptions.spacingMode === 'subtree-aware'
        ? Math.max(12, Math.round(computeBranchGap(nodeId, sortedChildren, nodeById, layoutOptions) * 0.2))
        : 0;

      let prevBounds = getSubtreeHorizontalBounds(
        sortedChildren[0],
        hierarchy,
        nodeById,
        currentPositions,
        newPositions,
      );

      for (let i = 1; i < sortedChildren.length; i += 1) {
        const childId = sortedChildren[i];
        const currentBounds = getSubtreeHorizontalBounds(
          childId,
          hierarchy,
          nodeById,
          currentPositions,
          newPositions,
        );
        const minLeft = prevBounds.right + requiredGap;
        if (currentBounds.left < minLeft) {
          const deltaX = minLeft - currentBounds.left;
          moveSubtreeXY(childId, deltaX, 0, hierarchy, currentPositions, newPositions);
          prevBounds = { left: currentBounds.left + deltaX, right: currentBounds.right + deltaX };
        } else {
          prevBounds = currentBounds;
        }
      }
    }
  };

  processNode(parentId, parentPos);
  return newPositions;
}

/**
 * Moves an entire subtree by deltaX and deltaY, updating the newPositions map.
 * Uses iterative approach to avoid stack overflow.
 */
function moveSubtreeXY(
  rootId: string,
  deltaX: number,
  deltaY: number,
  hierarchy: HierarchyMaps,
  currentPositions: Map<string, Point2D>,
  newPositions: Map<string, Point2D>
): void {
  const stack = [rootId];
  const visited = new Set<string>();

  while (stack.length > 0) {
    const nodeId = stack.pop()!;
    if (visited.has(nodeId)) continue;
    visited.add(nodeId);
    const currentPos = newPositions.get(nodeId) ?? currentPositions.get(nodeId);

    if (currentPos) {
      newPositions.set(nodeId, {
        x: currentPos.x + deltaX,
        y: currentPos.y + deltaY,
      });
    }

    const children = hierarchy.childrenByParent.get(nodeId) ?? [];
    children.forEach(childId => stack.push(childId));
  }
}

/**
 * Gets the ancestor path from a node up to the root.
 * Returns array with node first, then parent, grandparent, etc.
 */
export function getAncestorPath(nodeId: string, hierarchy: HierarchyMaps): string[] {
  const path: string[] = [nodeId];
  let currentId = hierarchy.parentByChild.get(nodeId);

  while (currentId) {
    path.push(currentId);
    currentId = hierarchy.parentByChild.get(currentId);
  }

  return path;
}

/**
 * Finds the highest ancestor that would be affected by a change at the given node.
 * Uses early termination: stops when subtree width doesn't change.
 *
 * @param nodeId - The node where change occurred
 * @param hierarchy - Parent-child relationship maps
 * @param widths - Current subtree widths
 * @param nodeById - Map of node ID to Node object
 * @param nodeSpacing - Horizontal spacing between siblings
 * @returns ID of the highest affected ancestor
 */
export function findHighestAffectedAncestor(
  nodeId: string,
  hierarchy: HierarchyMaps,
  widths: Map<string, number>,
  nodeById: Map<string, Node>,
  nodeSpacing: number = DEFAULT_NODE_SPACING,
): string {
  const ancestorPath = getAncestorPath(nodeId, hierarchy);
  let highestAffected = nodeId;
  const WIDTH_EPSILON = 1;

  // Walk up from changed node toward root
  for (let i = 0; i < ancestorPath.length - 1; i++) {
    const parentId = ancestorPath[i + 1];
    const oldWidth = widths.get(parentId);

    if (!oldWidth) {
      // No cached width - mark as affected
      highestAffected = parentId;
      continue;
    }

    // Calculate what the new width would be (using shared formula for consistency)
    const newWidth = computeNodeSubtreeWidth(parentId, hierarchy, widths, nodeById, nodeSpacing);

    // Check if width changed at this level
    if (Math.abs(newWidth - oldWidth) > WIDTH_EPSILON) {
      highestAffected = parentId;
      // Continue up - might affect higher levels
    } else {
      // Width unchanged - STOP (early termination)
      break;
    }
  }

  return highestAffected;
}

/**
 * Root placement strategy for computeFullTreeLayout.
 *
 * - 'auto'     — roots are placed sequentially from x=0, each at its
 *                subtree-centered offset; root y is reset to 0. Used when
 *                the tree has been structurally mutated (scenarios, csv
 *                import, fork, "as-of" reflow) and authored coords no
 *                longer apply. Note: this snaps root y to 0 even if the
 *                authored snapshot had a non-zero y. That divergence from
 *                'preserve' is intentional — `auto` is for re-layouts
 *                where authored coords are no longer trustworthy.
 * - 'preserve' — roots keep their authored x/y; only descendants are
 *                recentered relative to the authored root. Used by the
 *                read-only viewer's first paint so authored snapshot
 *                positions survive. Roots without authored coords are
 *                placed to the right of the previous root's subtree
 *                using the same `nextRootX` cursor as `auto`, so a
 *                partially-authored multi-root tree never has roots
 *                overlap.
 */
export type RootPlacement = 'auto' | 'preserve';

/**
 * Computes a full tree layout for all org nodes using the same algorithm
 * as the incremental snap-to-tree layout (recenterChildren).
 *
 * Finds all root nodes, then positions each tree top-down with children
 * centered under their parents. Multiple roots are laid out side by side.
 */
export function computeFullTreeLayout(
  nodes: Node[],
  edges: Edge[],
  options?: TreeLayoutOptions & { rootPlacement?: RootPlacement },
): Map<string, Point2D> {
  const layoutOptions = resolveTreeLayoutOptions(options);
  const rootPlacement: RootPlacement = options?.rootPlacement ?? 'auto';
  const orgNodeIds = new Set<string>();
  const nodeById = new Map<string, Node>();
  const currentPositions = new Map<string, Point2D>();

  nodes.forEach(node => {
    if (isOrgNode(node)) {
      orgNodeIds.add(node.id);
      nodeById.set(node.id, node);
      currentPositions.set(node.id, { x: node.x, y: node.y });
    }
  });

  if (orgNodeIds.size === 0) return new Map();

  const hierarchy = buildHierarchyMaps(orgNodeIds, edges, currentPositions);
  const subtreeOffsets = new Map<string, number>();
  const widths = calculateSubtreeWidths(hierarchy, nodeById, layoutOptions.nodeSpacing, subtreeOffsets, layoutOptions);

  const allPositions = new Map<string, Point2D>();

  // Layout each root tree, placing multiple roots side by side
  let nextRootX = 0;
  const ROOT_GAP = layoutOptions.rootGap;

  hierarchy.rootIds.forEach(rootId => {
    const rootNode = nodeById.get(rootId);
    const rootWidth = getNodeWidth(rootNode);
    const subtreeWidth = widths.get(rootId) ?? rootWidth;
    const rootOffset = subtreeOffsets.get(rootId) ?? (subtreeWidth - rootWidth) / 2;
    const fallbackPos: Point2D = { x: nextRootX + rootOffset, y: 0 };

    let rootPos: Point2D;
    if (rootPlacement === 'preserve') {
      const authored = currentPositions.get(rootId);
      // Coerce undefined / NaN to the auto fallback so partial authoring
      // never lets two roots collide. `currentPositions` always has an
      // entry (Node.x / .y are required), but defensive in case the
      // authored value is NaN from a corrupt snapshot.
      const ax = Number.isFinite(authored?.x) ? authored!.x : fallbackPos.x;
      const ay = Number.isFinite(authored?.y) ? authored!.y : fallbackPos.y;
      rootPos = { x: ax, y: ay };
    } else {
      rootPos = fallbackPos;
    }
    allPositions.set(rootId, rootPos);

    // Position all descendants
    const childPositions = recenterChildren(
      rootId,
      rootPos,
      hierarchy,
      nodeById,
      widths,
      currentPositions,
      layoutOptions.nodeSpacing,
      subtreeOffsets,
      layoutOptions,
    );
    childPositions.forEach((pos, id) => allPositions.set(id, pos));

    nextRootX += subtreeWidth + ROOT_GAP;
  });

  return allPositions;
}
